import { z } from 'zod';
import { ORG_TYPES } from './orgs';

/*
 * Organization profiles: what an org says about itself (description, links,
 * contact, logo), its public page at /o/:slug and the directory at /o.
 */

export const ORG_TAGLINE_MAX = 160;
export const ORG_ABOUT_MAX = 2000;
export const ORG_SLUG_MAX = 60;
/** Logos are square, at least this many pixels on a side. */
export const LOGO_MIN_PX = 200;
/** What the browser may pick before cropping (the server only ever gets a cropped PNG). */
export const LOGO_MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
export const LOGO_SIZES = [64, 128, 400] as const;
export type LogoSize = (typeof LOGO_SIZES)[number];
export const ORG_DIRECTORY_PAGE_SIZE = 24;

/** Where an org's logo is served (public, immutable: a new logo gets a new id). */
export const logoUrl = (logoId: string | null, size: LogoSize = 128) =>
  logoId ? `/v1/public/org-logos/${logoId}/${size}.png` : null;

/** Lowercase letters, digits and single hyphens: "iit-hyderabad". */
export const orgSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Use at least 3 characters')
  .max(ORG_SLUG_MAX)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and single hyphens');

const httpsUrl = z
  .string()
  .trim()
  .max(300)
  .url('Enter a full link, like https://example.org')
  .refine((u) => u.startsWith('https://'), 'Use an https:// link');

/** Optional text: blank becomes null. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => v || null);

/** International format with the country code: "+91 98765 43210" is stored as +919876543210. */
export const phoneSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s().-]/g, ''))
  .refine((v) => /^\+[1-9]\d{6,14}$/.test(v), 'Include the country code, like +91 98765 43210');

export const ORG_SOCIAL_NETWORKS = ['linkedin', 'x', 'youtube', 'instagram', 'facebook'] as const;
export type OrgSocialNetwork = (typeof ORG_SOCIAL_NETWORKS)[number];
export const ORG_SOCIAL_LABELS: Record<OrgSocialNetwork, string> = {
  linkedin: 'LinkedIn',
  x: 'X',
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook: 'Facebook',
};
const SOCIAL_HOSTS: Record<OrgSocialNetwork, RegExp> = {
  linkedin: /^https:\/\/([a-z]+\.)?linkedin\.com\//,
  x: /^https:\/\/(www\.)?(x|twitter)\.com\//,
  youtube: /^https:\/\/(www\.|m\.)?(youtube\.com|youtu\.be)\//,
  instagram: /^https:\/\/(www\.)?instagram\.com\//,
  facebook: /^https:\/\/([a-z]+\.)?facebook\.com\//,
};
export type OrgSocialLinks = Partial<Record<OrgSocialNetwork, string>>;

const socialLink = (n: OrgSocialNetwork) =>
  z
    .union([httpsUrl, z.literal('')])
    .nullish()
    .transform((v) => v || null)
    .refine((u) => !u || SOCIAL_HOSTS[n].test(u), `Use a ${ORG_SOCIAL_LABELS[n]} link`);

const socialLinksSchema = z
  .object({
    linkedin: socialLink('linkedin'),
    x: socialLink('x'),
    youtube: socialLink('youtube'),
    instagram: socialLink('instagram'),
    facebook: socialLink('facebook'),
  })
  .partial()
  .transform((links) => {
    const out: OrgSocialLinks = {};
    for (const n of ORG_SOCIAL_NETWORKS) if (links[n]) out[n] = links[n]!;
    return out;
  });

/** What an org's admins edit on Settings → Profile. */
export const orgProfileInputSchema = z.object({
  slug: orgSlugSchema,
  tagline: optionalText(ORG_TAGLINE_MAX),
  about: optionalText(ORG_ABOUT_MAX),
  websiteUrl: z
    .union([httpsUrl, z.literal('')])
    .nullish()
    .transform((v) => v || null),
  contactEmail: z
    .union([z.string().trim().toLowerCase().email().max(254), z.literal('')])
    .nullish()
    .transform((v) => v || null),
  contactPhone: z
    .union([phoneSchema, z.literal('')])
    .nullish()
    .transform((v) => v || null),
  city: optionalText(80),
  socialLinks: socialLinksSchema.default({}),
  /** Anyone can see the profile at /o/:slug and find it in the directory; otherwise members only. */
  profilePublic: z.boolean(),
  /** Email and phone on the public profile; otherwise only members see them. */
  contactPublic: z.boolean(),
});
export type OrgProfileInput = z.input<typeof orgProfileInputSchema>;

/** The org as named next to its courses, quizzes, events and certificates. */
export interface OrgBrand {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  verified: boolean;
  /** Whether /o/:slug is public (otherwise only members can open it). */
  profilePublic: boolean;
}

export const orgDirectoryQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  type: z.enum(ORG_TYPES).optional(),
  country: z
    .string()
    .regex(/^[A-Za-z]{2}$/)
    .transform((c) => c.toUpperCase())
    .optional(),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
  limit: z.coerce.number().int().min(1).max(60).default(ORG_DIRECTORY_PAGE_SIZE),
});
export type OrgDirectoryQuery = z.infer<typeof orgDirectoryQuerySchema>;

/** A slug from a name: "IIT Hyderabad (Main)" → "iit-hyderabad-main" (like the orgs_slug trigger, for suggestions). */
export function slugFromName(name: string): string {
  const s = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)
    .replace(/-+$/, '');
  return s.length >= 3 ? s : `org-${s || 'x'}`.slice(0, 50);
}

/**
 * A stored phone number (+919876543210) for reading: India and North America
 * in their usual groupings, others in groups of three or four.
 */
export function formatPhone(e164: string): string {
  const d = e164.replace(/^\+/, '');
  if (d.startsWith('91') && d.length === 12) return `+91 ${d.slice(2, 7)} ${d.slice(7)}`;
  if (d.startsWith('1') && d.length === 11)
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.startsWith('44') && d.length === 12) return `+44 ${d.slice(2, 6)} ${d.slice(6)}`;
  // Unknown plan: a 1–3 digit code can't be told apart reliably, so group the rest.
  const cc = d.slice(0, 2);
  const rest = d.slice(2).replace(/(\d{3,4})(?=\d{3})/g, '$1 ');
  return `+${cc} ${rest}`;
}
